import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend, Tooltip, ResponsiveContainer } from "recharts";

const TONE = { surge: "#D95D39", rise: "#4C956C" };
const ANGLE_TICK = { fill: "#cbd5e1", fontSize: 11, fontFamily: "IBM Plex Mono" };
const RADIUS_TICK = { fill: "#7c8699", fontSize: 10 };
const RADIUS_DOMAIN = [0, 100];
const TOOLTIP_STYLE = { background: "#0B172A", border: "1px solid rgba(255,255,255,0.2)", color: "#F4F1EA" };
const LEGEND_STYLE = { fontSize: 11, color: "#cbd5e1" };

export default function PredictiveRadar() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/radar/forecast").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-cream/60 font-mono-data text-sm">Loading radar…</div>;

  return (
    <div data-testid="predictive-radar-module" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 border border-white/10 bg-[#13233F] p-6 relative min-h-[540px]">
        <div className="absolute inset-0 chakra-radial opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#E8B298]">AI Predictive Radar</div>
              <div className="font-serif-display text-xl mt-1">Emerging competency demand</div>
            </div>
            <div className="flex items-center gap-3 text-[10px] font-mono-data uppercase tracking-[0.18em] text-cream/50">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#6b7280]"/>Current</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#0077B6]"/>6m forecast</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#D95D39]"/>12m forecast</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={460}>
            <RadarChart data={data.competencies} outerRadius="75%">
              <PolarGrid stroke="rgba(255,255,255,0.15)" />
              <PolarAngleAxis dataKey="axis" tick={ANGLE_TICK} />
              <PolarRadiusAxis angle={30} domain={RADIUS_DOMAIN} tick={RADIUS_TICK} stroke="rgba(255,255,255,0.1)" />
              <Radar name="Current"     dataKey="current"      stroke="#9ca3af" fill="#9ca3af" fillOpacity={0.18}/>
              <Radar name="6m forecast" dataKey="forecast_6m"  stroke="#0077B6" fill="#0077B6" fillOpacity={0.22}/>
              <Radar name="12m forecast" dataKey="forecast_12m" stroke="#D95D39" fill="#D95D39" fillOpacity={0.28}/>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="border border-white/10 bg-[#13233F] p-6 h-full">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#E8B298]">Emerging signals</div>
          <div className="font-serif-display text-xl mt-1 mb-5">What CBPs are telling us</div>
          <ul className="space-y-3">
            {data.emerging_signals.map((s,i) => (
              <li key={i} data-testid={`radar-signal-${i}`}
                  className="border border-white/10 p-4 hover:border-[#E8B298]/40 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-serif-display text-lg leading-tight">{s.signal}</span>
                  <span className="font-mono-data text-sm" style={{color: TONE[s.tone]}}>+{s.delta_pct}%</span>
                </div>
                <div className="mt-2 h-1.5 w-full bg-white/5">
                  <div className="h-full" style={{width: Math.min(100, s.delta_pct/4) + "%", background: TONE[s.tone]}}/>
                </div>
                <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-cream/50 mt-2">
                  Signal tone · {s.tone}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-6 border-t border-white/10 pt-4 text-xs text-cream/60 leading-relaxed">
            Forecasts derived from aggregated CBPs, district asks and feedback on delivered programmes.
            The radar shifts capacity building from reactive to proactive.
          </div>
        </div>
      </div>
    </div>
  );
}
